import React, { useState } from 'react';
import styles from './BackLayCalculator.module.css';
import {clearFormatting, handleBlurCurrency, handleBlurPercentage, handleFocus } from '../../../utils/calc-utils';

const BackLayCalculator = () => {
  const [fields, setFields] = useState({
    betType: 'SNR Bonus Bet',
    stake: '',
    backOdds: '',
    layOdds: '',
    commissionRate: '6.00%',
  });
  const [result, setResult] = useState('');
  const [isResultVisible, setIsResultVisible] = useState(false);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFields((prevFields) => ({ ...prevFields, [id]: value }));
  };

  const handleCalculate = () => {
    const { betType, stake, backOdds, layOdds, commissionRate } = fields;
    const parsedStake = clearFormatting(stake);
    const parsedBackOdds = parseFloat(backOdds);
    const parsedLayOdds = parseFloat(layOdds);
    const parsedCommissionRate = clearFormatting(commissionRate);
    let profit;

    if (!stake || !backOdds || !layOdds || !commissionRate) {
      setResult('Finish entering all your bet details before calculating');
    } else {
      if (betType === 'SNR Bonus Bet') {
        profit = (parsedStake * (parsedBackOdds - 1)) - (parsedStake * (parsedBackOdds - 1)) / (1 + ((1 / (parsedLayOdds - 1)) * (1 - parsedCommissionRate / 100)));
      } else if (betType === 'SR Bonus Bet') {
        profit = (parsedStake * parsedBackOdds) - (parsedStake * parsedBackOdds) / (1 + ((1 / (parsedLayOdds - 1)) * (1 - parsedCommissionRate / 100)));
      } else if (betType === 'Cash Bet') {
        const layStake = (parsedStake * parsedBackOdds) / (parsedLayOdds - parsedCommissionRate / 100);
        profit = layStake * (1 - parsedCommissionRate / 100) - parsedStake;
      }

      let resultHTML;
      if (betType === 'Cash Bet') {
        const layStake = (parsedStake * parsedBackOdds) / (parsedLayOdds - parsedCommissionRate / 100);
        const profitText = profit >= 0 ? 'profit' : 'loss';
        const profitClass = profit >= 0 ? styles.profitTextGreen : styles.profitTextRed;
        const absoluteProfit = Math.abs(profit);
        resultHTML = `Lay $${layStake.toFixed(2)} to lock-in a <span class="${profitClass}">${profitText} of $${absoluteProfit.toFixed(2)}</span> (${((profit / parsedStake) * 100).toFixed(2)}%). The liability of this lay bet will be $${(layStake * (parsedLayOdds - 1)).toFixed(2)}`;
      } else {
        resultHTML = `Lay $${(profit / (1 - parsedCommissionRate / 100)).toFixed(2)} to ensure a conversion of <span class="${profit >= 0 ? styles.profitTextGreen : styles.profitTextRed}">$${profit.toFixed(2)}</span> (a ${(profit / parsedStake * 100).toFixed(2)}% conversion). The liability of this lay bet will be $${((profit / (1 - parsedCommissionRate / 100)) * (parsedLayOdds - 1)).toFixed(2)}`;
      }
      setResult(resultHTML);
    }
    setIsResultVisible(true);
  };

  return (
    <div className={styles.backLayCalculator}>
      <h1 className={styles.calculatorTitle}>Back/Lay Calculator</h1>
      <div className={styles.containerWrapper}>
        <div className={styles.container}>
          <div className={styles.box}>
            <span className={styles.label}>Bet Type:</span>
            <select
              className={styles.dropdown}
              id="betType"
              value={fields.betType}
              onChange={handleInputChange}
            >
              <option value="SNR Bonus Bet">SNR Bonus Bet</option>
              <option value="SR Bonus Bet">SR Bonus Bet</option>
              <option value="Cash Bet">Cash Bet</option>
            </select>
          </div>
          <div className={styles.box}>
            <span className={styles.label}>Stake($):</span>
            <input
              className={`${styles.input} ${styles.stake}`}
              type="text"
              id="stake"
              value={fields.stake}
              onChange={handleInputChange}
              onBlur={(e) => handleBlurCurrency(e, setFields)}
              onFocus={(e) => handleFocus(e, setFields)}
              placeholder="Enter stake"
            />
          </div>
          <div className={styles.box}>
            <span className={styles.label}>Back Odds:</span>
            <input
              className={`${styles.input} ${styles.backOdds}`}
              type="number"
              id="backOdds"
              value={fields.backOdds}
              onChange={handleInputChange}
              placeholder="Enter back odds"
            />
          </div>
          <div className={styles.box}>
            <span className={styles.label}>Lay Odds:</span>
            <input
              className={`${styles.input} ${styles.layOdds}`}
              type="number"
              id="layOdds"
              value={fields.layOdds}
              onChange={handleInputChange}
              placeholder="Enter lay odds"
            />
          </div>
          <div className={styles.box}>
            <span className={styles.label}>Commission Rate (%):</span>
            <input
              className={styles.input}
              type="text"
              id="commissionRate"
              value={fields.commissionRate}
              onChange={handleInputChange}
              onBlur={(e) => handleBlurPercentage(e, setFields)}
              onFocus={(e) => handleFocus(e, setFields)}
              placeholder="Enter commission rate"
            />
          </div>
          <button onClick={handleCalculate}>Calculate</button>
          <div className={`${styles.result} ${isResultVisible ? styles.resultVisible : ''}`} id="result" dangerouslySetInnerHTML={{ __html: result }}></div>
        </div>
        <div className={styles.howToUse}>
          <h2>How to Use This Calculator</h2>
          <p>1. <strong>Select Bet Type:</strong> Choose the type of bet from the dropdown menu (e.g., SNR Bonus Bet).</p>
          <p>2. <strong>Enter Stake ($):</strong> Input the amount you are staking in the "Stake($)" field.</p>
          <p>3. <strong>Enter Back Odds:</strong> Input the back odds in the "Back Odds" field.</p>
          <p>4. <strong>Enter Lay Odds:</strong> Input the lay odds in the "Lay Odds" field.</p>
          <p>5. <strong>Commission Rate (%):</strong> Typically 6% for sports (except NRL 10%) and 8% for Horses in Australia but it varies between states. For further information, visit <a href="https://www.betfair.com.au/hub/help/commissions-charges/" style={{ color: '#6495ED', textDecoration: 'none' }}>Betfair Commissions and Charges</a>.</p>
          <p><strong>Reminder:</strong> Check liquidity on Betfair before placing a bet. This calculator is meant to be used with Betfair.</p>
        </div>
      </div>
    </div>
  );
};

export default BackLayCalculator;